import { useState } from "react";
import { Link } from "react-router-dom";
import { 
  Calendar as CalendarIcon, 
  ChevronLeft, 
  ChevronRight, 
  Clock, 
  MapPin, 
  Plus, 
  Share2 
} from "lucide-react";

const CalendarDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());

  const daysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
  const firstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

  const days = ["S", "M", "T", "W", "T", "F", "S"];
  const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

  const renderDays = () => {
    const totalDays = daysInMonth(currentDate.getFullYear(), currentDate.getMonth());
    const startDay = firstDayOfMonth(currentDate.getFullYear(), currentDate.getMonth());
    const result = [];

    // Padding for first week
    for (let i = 0; i < startDay; i++) {
      result.push(<div key={`pad-${i}`} className="cht-h-8"></div>);
    }

    for (let d = 1; d <= totalDays; d++) {
      const isToday = d === new Date().getDate() && currentDate.getMonth() === new Date().getMonth();
      const hasEvent = d === 28 || d === 30; // Mock events
      
      result.push(
        <div key={d} className="cht-flex cht-flex-col cht-items-center cht-justify-center cht-h-8 cht-relative">
          <span className={`cht-w-7 cht-h-7 cht-flex cht-items-center cht-justify-center cht-text-[11px] cht-font-black cht-rounded-full cht-transition-all ${
            isToday ? "cht-bg-black cht-text-white" : "cht-text-gray-900 hover:cht-bg-gray-100 cht-cursor-pointer"
          }`}>
            {d}
          </span>
          {hasEvent && (
            <div className={`cht-absolute -cht-bottom-0.5 cht-w-1 cht-h-1 cht-rounded-full ${d === 28 ? 'cht-bg-[#3e57da]' : 'cht-bg-orange-500'}`}></div>
          )}
        </div>
      );
    }
    return result;
  };

  return (
    <div className="cht-relative">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="cht-p-2 cht-rounded-[10px] cht-text-gray-500 hover:cht-bg-gray-100 hover:cht-text-[#3e57da] cht-transition-all cht-relative"
      >
        <CalendarIcon size={18} />
        <span className="cht-absolute cht-top-1 cht-right-1 cht-w-2 cht-h-2 cht-bg-[#3e57da] cht-rounded-full cht-animate-pulse"></span>
      </button>

      {isOpen && (
        <>
          <div 
            className="cht-fixed cht-inset-0 cht-z-40" 
            onClick={() => setIsOpen(false)}
          ></div>
          
          <div className="cht-absolute cht-right-0 cht-mt-4 cht-w-[340px] cht-bg-white/90 cht-backdrop-blur-2xl cht-border cht-border-white/20 cht-rounded-[32px] cht-shadow-[0_20px_60px_rgba(0,0,0,0.15)] cht-z-50 cht-overflow-hidden cht-animate-in cht-zoom-in-95 cht-duration-300">
            
            {/* Calendar Header */}
            <div className="cht-p-6 cht-pb-4 cht-flex cht-items-center cht-justify-between">
              <h4 className="cht-text-sm cht-font-black cht-text-black cht-uppercase cht-tracking-widest">
                {months[currentDate.getMonth()]} {currentDate.getFullYear()}
              </h4>
              <div className="cht-flex cht-gap-2">
                <button className="cht-p-1 cht-rounded-lg hover:cht-bg-gray-200/50"><ChevronLeft size={14} /></button>
                <button className="cht-p-1 cht-rounded-lg hover:cht-bg-gray-200/50"><ChevronRight size={14} /></button>
              </div>
            </div>

            {/* Date Grid */}
            <div className="cht-px-6 cht-pb-6">
              <div className="cht-grid cht-grid-cols-7 cht-text-center cht-mb-2">
                {days.map(d => (
                  <span key={d} className="cht-text-[10px] cht-font-black cht-text-gray-400">{d}</span>
                ))}
              </div>
              <div className="cht-grid cht-grid-cols-7 cht-gap-y-1">
                {renderDays()}
              </div>
            </div>

            {/* Today's Agenda */}
            <div className="cht-bg-gray-50/50 cht-p-6 cht-space-y-6">
              <div className="cht-flex cht-items-center cht-justify-between">
                <h5 className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-[0.2em]">Today's Agenda</h5>
                <Plus size={14} className="cht-text-gray-400 cursor-pointer hover:cht-text-black" />
              </div>

              <div className="cht-space-y-4">
                {/* Event Item */}
                <div className="cht-flex cht-items-center cht-gap-4 cht-group cht-cursor-pointer">
                  <div className="cht-w-1.5 cht-h-12 cht-bg-[#3e57da] cht-rounded-full"></div>
                  <div className="cht-space-y-1 cht-flex-1">
                    <div className="cht-flex cht-items-center cht-justify-between">
                      <h6 className="cht-text-xs cht-font-black cht-text-black group-hover:cht-text-[#3e57da] transition-colors">Advanced Django Patterns</h6>
                      <span className="cht-text-[9px] cht-font-bold cht-text-blue-600 cht-bg-blue-50 cht-px-2 cht-py-0.5 cht-rounded-md">Starts in 45m</span>
                    </div>
                    <div className="cht-flex cht-items-center cht-gap-3 cht-text-[10px] cht-font-bold cht-text-gray-400 uppercase tracking-widest">
                      <span className="cht-flex cht-items-center cht-gap-1"><Clock size={10} /> 18:00 - 19:30</span>
                      <span className="cht-flex cht-items-center cht-gap-1"><MapPin size={10} /> Discord</span>
                    </div>
                  </div>
                </div>

                <div className="cht-flex cht-items-center cht-gap-4 cht-group cht-cursor-pointer">
                  <div className="cht-w-1.5 cht-h-12 cht-bg-orange-500 cht-rounded-full"></div>
                  <div className="cht-space-y-1 cht-flex-1">
                    <h6 className="cht-text-xs cht-font-black cht-text-black group-hover:cht-text-orange-500 transition-colors">React Project Submission</h6>
                    <div className="cht-flex cht-items-center cht-gap-3 cht-text-[10px] cht-font-bold cht-text-gray-400 uppercase tracking-widest">
                      <span className="cht-flex cht-items-center cht-gap-1"><Clock size={10} /> By 23:59</span>
                      <span className="cht-flex cht-items-center cht-gap-1">Dashboard v1</span>
                    </div>
                  </div>
                </div>
              </div>

              <button className="cht-w-full cht-flex cht-items-center cht-justify-center cht-gap-2 cht-py-3 cht-bg-white cht-border cht-border-gray-100 cht-rounded-2xl cht-text-[10px] cht-font-black cht-uppercase cht-tracking-widest cht-shadow-sm hover:cht-bg-gray-50 cht-transition-all">
                <Share2 size={12} /> Sync to Google Calendar
              </button>

              <div className="cht-pt-4 cht-text-center">
                <Link 
                  to="/student/dashboard/event"
                  onClick={() => setIsOpen(false)}
                  className="cht-text-[10px] cht-font-black cht-uppercase cht-tracking-widest cht-text-gray-400 hover:cht-text-[#3e57da] transition-colors"
                >
                  View Full Schedule
                </Link>
              </div>
            </div>

          </div>
        </>
      )}
    </div>
  );
};

export default CalendarDropdown;
